/**
 * Price Review Module (TypeScript migrated)
 * Handles price review spinner management and price review requests (approve/retry).
 * Manages persistent spinner state, coordinates with API for price reviews,
 * and handles credit balance checks before retries.
 */

import { apiUrl } from './api';
import { clearCreditCache } from './credit';
import { hasInsufficientCredits } from './auth';
import { nowIso } from '@lotlingo/shared';
import { hasPricingData } from './rendering.js';
import { showToast, dismissToast } from './ui.js';
import type { State, Lot } from '../types';
import { TIMEOUTS } from '../../config/timeouts';
import { createLogger } from '../../shared/logger.js';
import { updatePriceStatusInPlace, updateEstimateCellInPlace } from './polling.js';

const log = createLogger('portal:price-review');

// =============================
// CONSTANTS
// =============================

/** How long price-check spinners run before auto-dismissing (15 minutes). */
const PRICE_CHECK_SPINNER_MS = TIMEOUTS.PRICE_CHECK_SPINNER_MS;

/** Grace period before starting spinner to prevent flicker for fast responses. */
const GRACE_MS = TIMEOUTS.PRICE_CHECK_GRACE_MS;

/** localStorage key for pending price review state. */
const PENDING_PRICE_REVIEW_STORAGE = 'lotlingo_pending_price_review_v2';

// =============================
// SPINNER STATE
// =============================

/** Tracks active spinner timers by lot ID. */
const priceReviewTimerById: Record<string, ReturnType<typeof setTimeout>> = Object.create(null);

// =============================
// PERSISTENCE HELPERS
// =============================

function loadPendingPriceReviewMap(): Record<string, number> {
  try {
    const raw = window.localStorage.getItem(PENDING_PRICE_REVIEW_STORAGE);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch (err) {
    log.warn('Failed to load pending price review map:', err);
    return {};
  }
}

function persistPendingPriceReview(map: Record<string, number>): void {
  try {
    window.localStorage.setItem(PENDING_PRICE_REVIEW_STORAGE, JSON.stringify(map));
  } catch (err) {
    log.warn('Failed to persist pending price review map:', err);
  }
}

function removePendingPriceReview(lotId: string): void {
  const map = loadPendingPriceReviewMap();
  delete map[lotId];
  persistPendingPriceReview(map);
}

// =============================
// SPINNER MANAGEMENT
// =============================

export function rehydratePendingPriceReviewFromStorage(state: State, render: () => void): void {
  const map = loadPendingPriceReviewMap();
  const now = Date.now();
  const threshold = GRACE_MS + PRICE_CHECK_SPINNER_MS;
  Object.keys(map).forEach(lotId => {
    const startTime = map[lotId];
    const elapsed = now - startTime;
    if (elapsed < threshold) {
      const remaining = Math.max(1, threshold - elapsed);
      state.pendingPriceReviewIds = state.pendingPriceReviewIds || new Set<string>();
      state.pendingPriceReviewIds.add(lotId);
      const lot = state.lots.find(item => item.id === lotId);
      if (lot && !hasPricingData(lot)) {
        priceReviewTimerById[lotId] = setTimeout(() => {
          clearPriceReviewSpinner(lotId, state, render);
        }, remaining);
      } else {
        delete map[lotId];
        state.pendingPriceReviewIds.delete(lotId);
      }
    } else {
      delete map[lotId];
    }
  });
  persistPendingPriceReview(map);
  render();
}

export function startPriceReviewSpinner(lotId: string, state: State, render: () => void): void {
  if (!lotId) return;
  state.pendingPriceReviewIds = state.pendingPriceReviewIds || new Set<string>();
  state.pendingPriceReviewIds.add(lotId);
  const map = loadPendingPriceReviewMap();
  map[lotId] = Date.now();
  persistPendingPriceReview(map);
  if (priceReviewTimerById[lotId]) {
    clearTimeout(priceReviewTimerById[lotId]);
  }
  priceReviewTimerById[lotId] = setTimeout(() => {
    clearPriceReviewSpinner(lotId, state, render);
  }, PRICE_CHECK_SPINNER_MS);
  // Try in-place update first, fall back to full render
  const lot = state.lots.find(item => item.id === lotId);
  if (!lot || !updatePriceStatusInPlace(lotId, lot)) {
    render();
  }
}

export function clearPriceReviewSpinner(lotId: string, state: State, render: () => void): void {
  if (!lotId) return;
  if (state.pendingPriceReviewIds) {
    state.pendingPriceReviewIds.delete(lotId);
  }
  if (priceReviewTimerById[lotId]) {
    clearTimeout(priceReviewTimerById[lotId]);
    delete priceReviewTimerById[lotId];
  }
  removePendingPriceReview(lotId);
  // Try in-place update first, fall back to full render
  const lot = state.lots.find(item => item.id === lotId);
  if (!lot || !updatePriceStatusInPlace(lotId, lot)) {
    render();
  }
}

export function updatePriceReviewSpinnersFromData(state: State, render: () => void): void {
  if (!state || !state.pendingPriceReviewIds || state.pendingPriceReviewIds.size === 0) {
    return;
  }
  const idsToRemove: string[] = [];
  state.pendingPriceReviewIds.forEach(lotId => {
    const lot = state.lots.find(item => item.id === lotId);
    if (lot && hasPricingData(lot)) {
      idsToRemove.push(lotId);
    }
  });
  if (idsToRemove.length) {
    idsToRemove.forEach(id => clearPriceReviewSpinner(id, state, render));
  }
}

// =============================
// PRICE REVIEW REQUEST HANDLER
// =============================

export async function handlePriceReview(
  lot: Lot,
  action: 'price-ack' | 'price-retry',
  state: State,
  render: () => void,
  showToastFn: typeof showToast,
  dismissToastFn: typeof dismissToast,
  openCheckoutModal: () => void,
  applyRemoteSnapshot: (lots: Lot[]) => void,
  applyModeBanner: () => void,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>,
  mapRemoteDocument: (data: Record<string, unknown>) => Lot | null | undefined,
  DEBUG: boolean
): Promise<void> {
  if (DEBUG) log.debug('handlePriceReview called', { lotId: lot && lot.id, action });
  if (!lot || !lot.id) {
    if (DEBUG) log.warn('handlePriceReview: no lot or lot.id');
    return;
  }
  if (state.mode !== 'auth0') {
    if (DEBUG) log.warn('handlePriceReview: not in auth0 mode');
    showToastFn('Price review requires a LotLingo account.', { type: 'warning' });
    return;
  }
  if (state.pendingPriceReviewIds && state.pendingPriceReviewIds.has(lot.id)) {
    if (DEBUG) log.warn('handlePriceReview: request already pending for', lot.id);
    showToastFn('Price review request already in progress for this lot.', { type: 'info' });
    return;
  }
  const user = state.user;
  if (!user) {
    if (DEBUG) log.warn('handlePriceReview: no user');
    showToastFn('Sign in to review AI pricing.', { type: 'error' });
    return;
  }
  const isRetry = action === 'price-retry';
  if (isRetry && hasInsufficientCredits()) {
    if (DEBUG) log.warn('handlePriceReview: insufficient credits');
    showToastFn('Insufficient credits. Please purchase additional credits to continue.', {
      type: 'error',
    });
    if (window.confirm('You have 0 credits remaining. Would you like to purchase more credits?')) {
      openCheckoutModal();
    }
    return;
  }
  const desiredStatus = action === 'price-ack' ? 'successPrice' : 'failedPrice';
  if (DEBUG) log.debug('handlePriceReview: proceeding with', desiredStatus);
  if (isRetry) startPriceReviewSpinner(lot.id, state, render);

  const titleForToast = lot.title ? '"' + lot.title + '"' : 'Lot';
  const loadingMessage =
    desiredStatus === 'successPrice'
      ? 'Marking ' + titleForToast + ' as approved…'
      : 'Requesting new AI analysis for ' + titleForToast + '…';
  const loadingToastId = showToastFn(loadingMessage, { type: 'info', duration: 10000 });

  const previousState = {
    priceReview: lot.priceReview ? JSON.parse(JSON.stringify(lot.priceReview)) : undefined,
    aiStatus: lot.aiStatus,
    aiAnalysis: ((): unknown => {
      if (!lot.aiAnalysis) return lot.aiAnalysis;
      try {
        return JSON.parse(JSON.stringify(lot.aiAnalysis));
      } catch {
        return lot.aiAnalysis;
      }
    })(),
    aiError: lot.aiError,
    priceStatus: lot.priceStatus,
    lowSellValue: lot.lowSellValue,
    highSellValue: lot.highSellValue,
    lowSellValueRaw: lot.lowSellValueRaw,
    highSellValueRaw: lot.highSellValueRaw,
    overlayPriceReview: lot.userOverlay
      ? Object.prototype.hasOwnProperty.call(lot.userOverlay, 'priceReview')
        ? lot.userOverlay.priceReview
          ? JSON.parse(JSON.stringify(lot.userOverlay.priceReview))
          : null
        : '__absent__'
      : undefined,
    overlayUpdatedAt:
      lot.userOverlay && typeof lot.userOverlay.updatedAt === 'string'
        ? lot.userOverlay.updatedAt
        : undefined,
  };

  if (desiredStatus === 'successPrice') {
    lot.priceReview = Object.assign({}, lot.priceReview || {}, {
      status: 'successPrice',
      approved: true,
    });
  } else {
    lot.priceReview = Object.assign({}, lot.priceReview || {}, { status: 'failedPrice' });
    lot.aiStatus = 'processing';
    lot.aiError = undefined;
  }
  if (lot.userOverlay) {
    lot.userOverlay.priceReview = lot.priceReview
      ? JSON.parse(JSON.stringify(lot.priceReview))
      : undefined;
    try {
      lot.userOverlay.updatedAt = nowIso();
    } catch {
      /* ignore */
    }
  }
  // Try in-place update first, fall back to full render
  if (!updatePriceStatusInPlace(lot.id, lot)) {
    render();
  }

  syncPriceReviewRemote(
    lot,
    desiredStatus,
    user,
    false,
    ensureAccessToken,
    mapRemoteDocument,
    DEBUG
  )
    .then(updated => {
      if (DEBUG) log.debug('syncPriceReviewRemote succeeded', { lotId: updated && updated.id });
      if (loadingToastId) dismissToastFn(loadingToastId);
      if (updated) {
        const index = state.lots.findIndex(item => item.id === updated.id);
        if (index >= 0) state.lots[index] = updated;
        else state.lots.unshift(updated);
        applyRemoteSnapshot(state.lots);
        updatePriceReviewSpinnersFromData(state, render);
        // Bust the credit cache so the options panel reflects the deducted
        // credit immediately rather than waiting up to 60 s for the next poll.
        if (isRetry) {
          clearCreditCache();
        }
        // Try in-place update first for both price status and estimate (AI may update estimates)
        const priceUpdated = updatePriceStatusInPlace(updated.id, updated);
        const estimateUpdated = updateEstimateCellInPlace(updated.id, updated);
        if (!priceUpdated && !estimateUpdated) {
          render();
        }
        const successMessage =
          desiredStatus === 'successPrice'
            ? 'Marked ' + titleForToast + ' as approved.'
            : 'Requested new AI analysis for ' + titleForToast + '.';
        showToastFn(successMessage, { type: 'success' });
      }
    })
    .catch(err => {
      if (DEBUG) log.error('syncPriceReviewRemote failed', err);
      if (loadingToastId) dismissToastFn(loadingToastId);
      if (isRetry) clearPriceReviewSpinner(lot.id, state, render);
      if (err && (err as any).status === 402) {
        clearCreditCache();
        restorePreviousLotState(lot, previousState);
        // Try in-place update first, fall back to full render
        if (!updatePriceStatusInPlace(lot.id, lot)) {
          render();
        }
        showToastFn('Insufficient credits. Please purchase additional credits to continue.', {
          type: 'error',
        });
        if (
          window.confirm('You have insufficient credits. Would you like to purchase more credits?')
        ) {
          openCheckoutModal();
        }
        log.error('Insufficient credits for price review');
        return;
      }
      restorePreviousLotState(lot, previousState);
      // Try in-place update first, fall back to full render
      if (!updatePriceStatusInPlace(lot.id, lot)) {
        render();
      }
      const message = err && err.message ? err.message : String(err);
      showToastFn('Failed to update price review: ' + message, { type: 'error' });
      log.error('Failed to update price review', err);
    })
    .finally(() => {
      if (DEBUG) log.debug('syncPriceReviewRemote finally, removing pending', lot.id);
      if (action === 'price-ack') clearPriceReviewSpinner(lot.id, state, render);
      // Use the current lot from state (may have been updated by the API response)
      const currentLot = state.lots.find(item => item.id === lot.id) || lot;
      // Try in-place update first, fall back to full render
      if (!updatePriceStatusInPlace(lot.id, currentLot)) {
        render();
      }
      applyModeBanner();
    });
}

import type { PriceReviewMetadata } from '../../types/api';

function restorePreviousLotState(
  lot: Lot,
  previous: {
    priceReview: PriceReviewMetadata | undefined;
    aiStatus: string | undefined;
    aiAnalysis: unknown;
    aiError: unknown;
    priceStatus: string | undefined;
    lowSellValue: number | undefined;
    highSellValue: number | undefined;
    lowSellValueRaw: string | number | undefined;
    highSellValueRaw: string | number | undefined;
    overlayPriceReview: PriceReviewMetadata | '__absent__' | undefined;
    overlayUpdatedAt: number | string | undefined;
  }
): void {
  lot.priceReview = previous.priceReview;
  lot.aiStatus = previous.aiStatus as
    | 'pending'
    | 'processing'
    | 'succeeded'
    | 'failed'
    | 'skipped'
    | undefined;
  lot.aiAnalysis = previous.aiAnalysis;
  lot.aiError = previous.aiError;
  lot.priceStatus = previous.priceStatus as
    | 'pending'
    | 'processing'
    | 'succeeded'
    | 'failed'
    | 'skipped'
    | undefined;
  lot.lowSellValue = previous.lowSellValue;
  lot.highSellValue = previous.highSellValue;
  lot.lowSellValueRaw = previous.lowSellValueRaw;
  lot.highSellValueRaw = previous.highSellValueRaw;
  if (lot.userOverlay) {
    if (previous.overlayPriceReview === '__absent__') {
      delete lot.userOverlay.priceReview;
    } else if (previous.overlayPriceReview !== undefined) {
      lot.userOverlay.priceReview = previous.overlayPriceReview
        ? JSON.parse(JSON.stringify(previous.overlayPriceReview))
        : previous.overlayPriceReview;
    }
    if (previous.overlayUpdatedAt) {
      lot.userOverlay.updatedAt = previous.overlayUpdatedAt;
    }
  }
}

// =============================
// API SYNC FUNCTION
// =============================

import type { PortalUser } from '../types';

export async function syncPriceReviewRemote(
  lot: Lot,
  desiredStatus: 'successPrice' | 'failedPrice',
  user: PortalUser,
  retry: boolean,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>,
  mapRemoteDocument: (data: Record<string, unknown>) => Lot | null | undefined,
  DEBUG: boolean
): Promise<Lot> {
  if (DEBUG)
    log.debug('syncPriceReviewRemote starting', {
      lotId: lot && lot.id,
      desiredStatus,
      retry,
    });
  const id = lot && lot.id;
  if (!id) {
    if (DEBUG) log.error('syncPriceReviewRemote: no lot id');
    throw new Error('Lot is missing an id.');
  }
  let token: string | null | undefined;
  try {
    token = await ensureAccessToken(Boolean(retry));
    if (DEBUG) log.debug('syncPriceReviewRemote: got token', !!token);
  } catch (err) {
    if (DEBUG) log.error('syncPriceReviewRemote: failed to get token', err);
    throw new Error('Unable to acquire access token.');
  }
  if (!token) {
    if (DEBUG) log.error('syncPriceReviewRemote: token is null/undefined');
    throw new Error('Unable to acquire access token.');
  }
  const endpoint = apiUrl('extensions/scrapes/' + encodeURIComponent(id));
  if (DEBUG) log.debug('syncPriceReviewRemote: endpoint', endpoint);
  const payload: any = {
    priceStatus: desiredStatus,
    retry: {
      priceStatus: desiredStatus,
      portalUserId: (user.sub || user.email || user.name || 'portal-user').toString(),
      portalUserEmail: user.email || undefined,
      portalUserName: user.name || undefined,
    },
  };
  const retryPayload: any = {};
  let retryPayloadCount = 0;
  const lotTitle = typeof lot.title === 'string' ? lot.title.trim() : '';
  if (lotTitle) {
    retryPayload.title = lotTitle;
    retryPayloadCount++;
  }
  const lotDescription =
    typeof lot.notes === 'string' && lot.notes.trim()
      ? lot.notes.trim()
      : typeof lot.description === 'string' && lot.description.trim()
        ? lot.description.trim()
        : '';
  if (lotDescription) {
    retryPayload.description = lotDescription || undefined;
    retryPayloadCount++;
  }
  const imageSet = new Set<string>();
  if (Array.isArray(lot.images)) {
    lot.images.forEach(img => {
      if (typeof img === 'string') {
        const trimmed = img.trim();
        if (trimmed) imageSet.add(trimmed);
      }
    });
  }
  if (typeof lot.imageUrl === 'string') {
    const trimmedSingle = lot.imageUrl.trim();
    if (trimmedSingle) imageSet.add(trimmedSingle);
  }
  if (imageSet.size > 0) {
    retryPayload.images = Array.from(imageSet);
    retryPayloadCount++;
  }
  if (retryPayloadCount > 0) {
    payload.retry.payload = retryPayload;
  }
  if (DEBUG) log.debug('PATCH', endpoint, payload);

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    throw err;
  }
  if (DEBUG) log.debug('PATCH status', response.status, 'ct', response.headers.get('content-type'));
  if (response.status === 401 && !retry) {
    return syncPriceReviewRemote(
      lot,
      desiredStatus,
      user,
      true,
      ensureAccessToken,
      mapRemoteDocument,
      DEBUG
    );
  }
  if (response.status === 402) {
    const credError: any = new Error('Insufficient credits');
    credError.status = 402;
    throw credError;
  }
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Failed to update price review (' + response.status + ')');
  }
  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  if (contentType && contentType.indexOf('json') === -1) {
    throw new Error('Unexpected response from API.');
  }
  const data = await response.json();
  const mapped = mapRemoteDocument(data);
  if (!mapped) {
    throw new Error('Received empty response from API.');
  }
  return mapped as Lot;
}
